import type {TFunction} from 'i18next';
import {LogoutRounded, ListAltOutlined, SettingsRounded, SupportAgentRounded} from '@mui/icons-material';
import type {AccessLevelPackagesWithAggregates} from '../../../core/services/access-levels';
import type {PanelEntry} from '../types';
import {createOperatorPanelEntries} from './operator';
import {createCustomerPanelEntries} from './customer';
import {createVendorPanelEntries} from './vendor';

export interface CreatePanelEntriesDeps {
    tSidebarDefault: TFunction<'components'>;
    tSidebarCustomer: TFunction<'components'>;
    tSidebarVendor: TFunction<'components'>;
    tCmdPages: TFunction<'components'>;
    tCmdActions: TFunction<'components'>;
    packages: AccessLevelPackagesWithAggregates;
    purchasingKeyForPlanning?: string | null;
}

/** Builds action-only catalog entries for the command palette. */
export const createActionPanelEntries = ({
    tCmdActions,
    packages,
}: Pick<CreatePanelEntriesDeps, 'tCmdActions' | 'packages'>): PanelEntry[] => [
    {
        id: 'action-settings',
        title: tCmdActions((t) => t.settings),
        subtitle: tCmdActions((t) => t.settingsSubtitle),
        keywords: ['settings', 'preferences', 'configuration'],
        icon: <SettingsRounded fontSize="small"/>,
        surfaces: ['command-search'],
        actionKey: 'settings',
        commandSearch: {providerId: 'panel-actions', category: 'actions', badge: 'Action', badgeColor: 'secondary'},
    },
    {
        id: 'action-support',
        title: tCmdActions((t) => t.support),
        subtitle: tCmdActions((t) => t.supportSubtitle),
        keywords: ['support', 'chat', 'help', 'crisp'],
        icon: <SupportAgentRounded fontSize="small"/>,
        surfaces: ['command-search'],
        actionKey: 'support',
        commandSearch: {providerId: 'panel-actions', category: 'actions', badge: 'Action', badgeColor: 'secondary'},
    },
    {
        id: 'action-logout',
        title: tCmdActions((t) => t.logout),
        subtitle: tCmdActions((t) => t.logoutSubtitle),
        keywords: ['logout', 'sign out', 'exit'],
        icon: <LogoutRounded fontSize="small"/>,
        surfaces: ['command-search'],
        actionKey: 'logout',
        commandSearch: {providerId: 'panel-actions', category: 'actions', badge: 'Action', badgeColor: 'secondary'},
    },
    {
        id: 'action-quick-order',
        title: tCmdActions((t) => t.quickOrder),
        subtitle: tCmdActions((t) => t.quickOrderSubtitle),
        keywords: ['quick', 'order', 'sales'],
        icon: <ListAltOutlined fontSize="small"/>,
        surfaces: ['command-search'],
        actionKey: 'quick-order',
        hide: () => !packages.sales.quickOrder,
        accessKey: 'sales.quickOrder',
        commandSearch: {providerId: 'panel-actions', category: 'actions', badge: 'Action', badgeColor: 'secondary'},
    },
];

/** Merges operator, customer, vendor, and action catalogs into one PANEL_ENTRIES list. */
export const createPanelEntries = (deps: CreatePanelEntriesDeps): PanelEntry[] => [
    ...createOperatorPanelEntries({
        t: deps.tSidebarDefault,
        tCmd: deps.tCmdPages,
        packages: deps.packages,
        purchasingKeyForPlanning: deps.purchasingKeyForPlanning,
    }),
    ...createCustomerPanelEntries({
        t: deps.tSidebarCustomer,
        tCmd: deps.tCmdPages,
        packages: deps.packages,
    }),
    ...createVendorPanelEntries({
        t: deps.tSidebarVendor,
        tCmd: deps.tCmdPages,
        packages: deps.packages,
    }),
    ...createActionPanelEntries({
        tCmdActions: deps.tCmdActions,
        packages: deps.packages,
    }),
];

export {createOperatorPanelEntries, createCustomerPanelEntries, createVendorPanelEntries};
